<template>
  <nav
    v-if="links && total > 1"
    class="flex items-center justify-between"
    role="navigation"
  >
    <div class="flex justify-between flex-1 sm:hidden">
      <span
        v-if="onFirstPage"
        class="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-gray-100 border border-gray-300 cursor-default leading-5"
      >
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            clip-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            fill-rule="evenodd"
          />
        </svg>
      </span>
      <component
        :is="controlTag"
        v-else
        :href="hrefFor(prevPage)"
        :type="buttonType"
        @click="handleClick(prevPage)"
        class="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
      >
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            clip-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            fill-rule="evenodd"
          />
        </svg>
      </component>

      <component
        :is="controlTag"
        v-if="hasMorePages"
        :href="hrefFor(nextPage)"
        :type="buttonType"
        @click="handleClick(nextPage)"
        class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
      >
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            clip-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            fill-rule="evenodd"
          />
        </svg>
      </component>
      <span
        v-else
        class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-500 bg-gray-100 border border-gray-300 cursor-default leading-5"
      >
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            clip-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            fill-rule="evenodd"
          />
        </svg>
      </span>
    </div>

    <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
      <div>
        <span class="relative z-0 inline-flex">
          <span
            v-if="onFirstPage"
            aria-disabled="true"
            aria-hidden="true"
            class="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-gray-100 border border-gray-300 cursor-default leading-5"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                fill-rule="evenodd"
              />
            </svg>
          </span>
          <component
            :is="controlTag"
            v-else
            :href="hrefFor(prevPage)"
            :type="buttonType"
            @click="handleClick(prevPage)"
            class="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 leading-5 hover:text-gray-400 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                fill-rule="evenodd"
              />
            </svg>
          </component>

          <div v-if="currentPage > 5">
            <component
              :is="controlTag"
              :href="hrefFor(1)"
              :type="buttonType"
              @click="handleClick(1)"
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            >
              1
            </component>

            <span
              aria-disabled="true"
              aria-hidden="true"
              class="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-gray-100 border border-gray-300 cursor-default leading-5"
            >
              ...
            </span>
          </div>

          <div v-if="currentPage > 1">
            <component
              :is="controlTag"
              :href="hrefFor(prevTwoPages)"
              :type="buttonType"
              @click="handleClick(prevTwoPages)"
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            >
              {{ prevTwoPages }}
            </component>
          </div>

          <div v-if="currentPage > 2">
            <component
              :is="controlTag"
              :href="hrefFor(prevPage)"
              :type="buttonType"
              @click="handleClick(prevPage)"
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            >
              {{ prevPage }}
            </component>
          </div>

          <!-- Current page -->
          <span
            aria-current="page"
            class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-gray-300 font-bold border border-gray-300 leading-5 cursor-default"
          >
            {{ currentPage }}
          </span>

          <div v-if="currentPage < total - 1">
            <component
              :is="controlTag"
              :href="hrefFor(nextPage)"
              :type="buttonType"
              @click="handleClick(nextPage)"
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            >
              {{ nextPage }}
            </component>
          </div>

          <div v-if="currentPage < total - 2">
            <component
              :is="controlTag"
              :href="hrefFor(nextTwoPages)"
              :type="buttonType"
              @click="handleClick(nextTwoPages)"
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            >
              {{ nextTwoPages }}
            </component>
          </div>

          <div v-if="currentPage < total - 5">
            <span
              aria-disabled="true"
              aria-hidden="true"
              class="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-gray-100 border border-gray-300 cursor-default leading-5"
            >
              ...
            </span>
          </div>

          <div v-if="currentPage < total - 4">
            <component
              :is="controlTag"
              :href="hrefFor(thirdLastPage)"
              :type="buttonType"
              @click="handleClick(thirdLastPage)"
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            >
              {{ thirdLastPage }}
            </component>
          </div>

          <div v-if="currentPage < total - 3">
            <component
              :is="controlTag"
              :href="hrefFor(secondLastPage)"
              :type="buttonType"
              @click="handleClick(secondLastPage)"
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            >
              {{ secondLastPage }}
            </component>
          </div>

          <div v-if="currentPage < total">
            <component
              :is="controlTag"
              :href="hrefFor(lastPage)"
              :type="buttonType"
              @click="handleClick(lastPage)"
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300 leading-5 hover:text-gray-500 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            >
              {{ lastPage }}
            </component>
          </div>

          <component
            :is="controlTag"
            v-if="hasMorePages"
            :href="hrefFor(nextPage)"
            :type="buttonType"
            @click="handleClick(nextPage)"
            class="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium text-gray-500 bg-white border border-gray-300 leading-5 hover:text-gray-400 focus:z-10 focus:outline-none focus:ring ring-gray-300 focus:border-blue-300 active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                fill-rule="evenodd"
              />
            </svg>
          </component>
          <span
            v-else
            aria-disabled="true"
            aria-hidden="true"
            class="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium text-gray-500 bg-gray-100 border border-gray-300 cursor-default leading-5"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                fill-rule="evenodd"
              />
            </svg>
          </span>
        </span>
      </div>
    </div>
  </nav>
</template>

<script>
export default {
  props: {
    links: Object,
    onPageChange: Function, // Optional callback for ajax pagination
  },
  computed: {
    // Pages navigate via an Inertia link by default, so the page component is
    // remounted and re-reads its props. Pages that pass onPageChange fetch the
    // new page themselves, so they get a plain button instead.
    controlTag() {
      return this.onPageChange ? "button" : "inertia-link";
    },
    buttonType() {
      return this.onPageChange ? "button" : null;
    },
    currentPage() {
      const parsed = parseInt(this.links?.current_page);
      if (isNaN(parsed)) return 1;
      return Math.min(this.total, Math.max(1, parsed));
    },
    total() {
      const parsed = parseInt(this.links?.total_pages);
      if (isNaN(parsed)) return 1;
      return Math.max(1, parsed);
    },
    onFirstPage() {
      return this.currentPage === 1;
    },
    hasMorePages() {
      return this.currentPage < this.total;
    },
    prevPage() {
      return Math.max(1, this.currentPage - 1);
    },
    nextPage() {
      return Math.min(this.total, this.currentPage + 1);
    },
    prevTwoPages() {
      return Math.max(1, this.currentPage - 2);
    },
    nextTwoPages() {
      return Math.min(this.total, this.currentPage + 2);
    },
    lastPage() {
      return this.total;
    },
    secondLastPage() {
      return Math.max(1, this.total - 1);
    },
    thirdLastPage() {
      return Math.max(1, this.total - 2);
    },
  },
  methods: {
    handleClick(page) {
      // Inertia links navigate on their own; only the ajax variant needs this.
      if (this.onPageChange) {
        this.onPageChange(page);
      }
    },
    hrefFor(page) {
      return this.onPageChange ? null : this.pageUrl(page);
    },
    pageUrl(pageTo) {
      // Keep every current query parameter (filters, sort) and swap the page.
      const [path, queryString] = this.$page.url.split("?");
      const params = new URLSearchParams(queryString);
      params.set("page", pageTo);
      return `${path}?${params.toString()}`;
    },
  },
};
</script>
